import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { resolveImage } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useContent, formatPrice } from "@/context/ContentContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Minus, Plus, ShieldCheck, Truck, BookOpenCheck } from "lucide-react";
import ShippingEstimator from "@/components/ShippingEstimator";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const { content } = useContent();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);

  useEffect(() => { api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setProduct(false)); }, [id]);

  if (product === false) return <div className="max-w-7xl mx-auto px-4 py-20 text-center">Product not found. <Link className="text-tmf-blue" to="/products">Browse products</Link></div>;
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-sm font-mono-tmf uppercase tracking-widest text-slate-500">Loading…</div>;

  const discount = Math.round(((product.mrp - product.factory_price) / product.mrp) * 100);

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="text-xs font-mono-tmf uppercase tracking-widest text-slate-500 mb-6">
        <Link to="/products" className="hover:text-tmf-blue">Products</Link> / <Link to={`/category/${product.category}`} className="hover:text-tmf-blue">{product.category.replace(/-/g, " ")}</Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        <div className="aspect-square border border-slate-200 bg-slate-50 overflow-hidden relative">
          <img src={resolveImage(product.image)} alt={product.name} className="w-full h-full object-cover" />
          {discount > 0 && (
            <div className="absolute top-0 left-0 bg-tmf-orange text-white text-xs font-bold uppercase tracking-widest px-3 py-2 font-mono-tmf">
              Factory · Save {discount}%
            </div>
          )}
        </div>

        <div>
          <div className="inline-flex w-fit text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue bg-blue-50 px-2 py-1 mb-2 border border-blue-100 font-bold">{product.category.replace(/-/g, " ")}</div>
          <h1 data-testid="product-title" className="font-display font-black text-3xl md:text-4xl uppercase tracking-tight leading-tight text-tmf-navy">{product.name}</h1>

          <div className="mt-6 border border-slate-300 p-5 bg-slate-50">
            <div className="text-[10px] font-mono-tmf uppercase tracking-widest text-tmf-blue mb-1">Factory-Direct Price</div>
            <div className="flex items-end gap-3">
              <div className="font-display font-black text-5xl text-tmf-navy">{formatPrice(product.factory_price, content)}</div>
              <div className="pb-2 text-slate-400 line-through">MRP {formatPrice(product.mrp, content)}</div>
              {discount > 0 && <div className="pb-2 text-tmf-orange font-bold text-sm">− {discount}%</div>}
            </div>
            <div data-testid="price-tax-note" className="text-xs text-slate-500 mt-2">
              <span className="font-bold text-tmf-navy">Inclusive of GST</span> · Shipping calculated at checkout (auto-billed by our logistics partner) · Inclusive of manufacturer warranty.
            </div>
          </div>

          <p className="mt-6 text-slate-700 leading-relaxed">{product.description}</p>

          <div className="mt-8 flex items-center gap-3">
            <div className="flex border border-tmf-navy h-12">
              <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 hover:bg-tmf-navy hover:text-white transition-colors"><Minus size={16} className="mx-auto" /></button>
              <div className="w-14 flex items-center justify-center font-mono-tmf font-bold">{qty}</div>
              <button data-testid="qty-increase" onClick={() => setQty(qty + 1)} className="w-11 hover:bg-tmf-navy hover:text-white transition-colors"><Plus size={16} className="mx-auto" /></button>
            </div>

            <Button
              data-testid="add-to-cart-btn"
              onClick={() => { addItem(product, qty); toast.success(`Added ${qty} × ${product.name} to cart`); }}
              className="rounded-none bg-tmf-navy hover:bg-tmf-blue text-white h-12 flex-1 text-sm font-bold uppercase tracking-widest"
            >
              Add to Cart
            </Button>
            <Button
              data-testid="buy-now-btn"
              onClick={() => { addItem(product, qty); nav("/cart"); }}
              className="rounded-none bg-tmf-blue hover:bg-tmf-navy text-white h-12 px-6 text-sm font-bold uppercase tracking-widest"
            >
              Buy Now
            </Button>
          </div>

          {product.bulk_qty > 0 && qty >= product.bulk_qty && (
            <div data-testid="product-bulk-banner" className="mt-4 border-l-4 border-amber-500 bg-amber-50 p-3 text-xs text-amber-900">
              <div className="flex items-center gap-2 font-bold uppercase tracking-widest text-[10px] mb-1">Bulk order detected</div>
              <div>You've selected {qty} units (≥ {product.bulk_qty}). You can proceed to checkout — <b>our sales team will contact you for a custom bulk quote</b> (up to 25% off factory price). No cap on order size.</div>
            </div>
          )}

          <div className="mt-8 grid grid-cols-3 gap-3 border-t border-slate-200 pt-6">
            <div className="flex flex-col gap-1"><ShieldCheck size={20} className="text-tmf-blue" /><div className="text-xs font-bold">1-3 Yr Warranty</div><div className="text-[10px] text-slate-500 font-mono-tmf">T&amp;C applied</div></div>
            <div className="flex flex-col gap-1"><Truck size={20} className="text-tmf-blue" /><div className="text-xs font-bold">Pan-India Shipping</div></div>
            <div className="flex flex-col gap-1"><BookOpenCheck size={20} className="text-tmf-blue" /><div className="text-xs font-bold">Setup Guide Included</div><div className="text-[10px] text-slate-500 font-mono-tmf">PDF + Video</div></div>
          </div>

          <div className="mt-6">
            <ShippingEstimator items={[{ product_id: product.product_id, quantity: qty }]} />
          </div>

          {product.specs && Object.keys(product.specs).length > 0 && (
            <div className="mt-8 border border-slate-200">
              <div className="bg-tmf-navy text-white font-display font-bold uppercase tracking-widest text-xs px-4 py-3">Specifications</div>
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(product.specs).map(([k, v]) => (
                    <tr key={k} className="border-t border-slate-200">
                      <td className="px-4 py-3 font-mono-tmf uppercase text-xs tracking-widest text-slate-500 w-1/3">{k}</td>
                      <td className="px-4 py-3 font-medium">{String(v)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
